/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as AuthenticationActionCreators from '@app/actions/AuthenticationActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import styles from '@app/components/auth/BrowserLoginHandoffModal.module.css';
import {HandoffCodeDisplay} from '@app/components/auth/HandoffCodeDisplay';
import {Input} from '@app/components/form/Input';
import * as Modal from '@app/components/modals/Modal';
import {Button} from '@app/components/uikit/button/Button';
import RuntimeConfigStore from '@app/stores/RuntimeConfigStore';
import {getElectronAPI, openExternalUrl} from '@app/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

interface LoginSuccessPayload {
	token: string;
	userId: string;
}

interface BrowserLoginHandoffModalProps {
	onSuccess: (payload: LoginSuccessPayload) => Promise<void>;
	targetWebAppUrl?: string;
	prefillEmail?: string;
}

const POLL_INTERVAL_MS = 2000;

function normalizeInstanceOrigin(raw: string): string {
	const trimmed = raw.trim();
	if (!trimmed) {
		throw new Error('Instance URL is required');
	}

	const candidate = /^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;

	const url = new URL(candidate);
	if (url.protocol !== 'https:' && url.protocol !== 'http:') {
		throw new Error('Instance URL must use http or https');
	}

	return url.origin;
}

const BrowserLoginHandoffModal = observer(
	({onSuccess, targetWebAppUrl, prefillEmail}: BrowserLoginHandoffModalProps) => {
		const {i18n} = useLingui();

		const electronApi = getElectronAPI();
		const switchInstanceUrl = electronApi?.switchInstanceUrl;
		const canSwitchInstanceUrl = typeof switchInstanceUrl === 'function';

		const currentWebAppUrl = RuntimeConfigStore.webAppBaseUrl;
		const [instanceUrl, setInstanceUrl] = useState(() => targetWebAppUrl ?? currentWebAppUrl);
		const [instanceUrlError, setInstanceUrlError] = useState<string | null>(null);

		const [handoffCode, setHandoffCode] = useState<string | null>(null);
		const [handoffExpiresAt, setHandoffExpiresAt] = useState<string | null>(null);
		const [isGenerating, setIsGenerating] = useState(false);
		const [error, setError] = useState<string | null>(null);

		const pollingRef = useRef(false);
		const completedRef = useRef(false);

		const instanceUrlHelper = useMemo(
			() => (canSwitchInstanceUrl ? i18n._(msg`The URL of the Fluxer instance you want to sign in to.`) : null),
			[canSwitchInstanceUrl, i18n],
		);

		const generateCode = useCallback(async () => {
			setIsGenerating(true);
			setError(null);
			setHandoffCode(null);
			setHandoffExpiresAt(null);

			try {
				const result = await AuthenticationActionCreators.initiateDesktopHandoff();
				setHandoffCode(result.code);
				setHandoffExpiresAt(result.expires_at);
			} catch (e) {
				setError(e instanceof Error ? e.message : String(e));
			} finally {
				setIsGenerating(false);
			}
		}, []);

		useEffect(() => {
			void generateCode();
		}, [generateCode]);

		useEffect(() => {
			if (!handoffCode || completedRef.current) return;

			pollingRef.current = true;
			const timer = setInterval(async () => {
				if (!pollingRef.current) return;

				try {
					const result = await AuthenticationActionCreators.pollDesktopHandoffStatus(handoffCode);

					if (result.status === 'completed' && result.token && result.user_id) {
						pollingRef.current = false;
						completedRef.current = true;
						await onSuccess({token: result.token, userId: result.user_id});
						ModalActionCreators.pop();
					} else if (result.status === 'expired') {
						pollingRef.current = false;
					}
				} catch {
					pollingRef.current = false;
				}
			}, POLL_INTERVAL_MS);

			return () => {
				pollingRef.current = false;
				clearInterval(timer);
			};
		}, [handoffCode, onSuccess]);

		const handleInstanceUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
			setInstanceUrl(e.target.value);
			setInstanceUrlError(null);
		}, []);

		const handleOpenBrowser = useCallback(async () => {
			const fallbackUrl = targetWebAppUrl || currentWebAppUrl;
			let baseUrl = fallbackUrl;

			if (canSwitchInstanceUrl && instanceUrl.trim()) {
				try {
					baseUrl = normalizeInstanceOrigin(instanceUrl);
				} catch {
					setInstanceUrlError(
						i18n._(msg`Invalid instance URL. Try something like "example.com" or "https://example.com".`),
					);
					return;
				}

				if (baseUrl !== window.location.origin && handoffCode) {
					try {
						await switchInstanceUrl({
							instanceUrl: baseUrl,
							desktopHandoffCode: handoffCode,
						});
					} catch (switchError) {
						const detail = switchError instanceof Error ? switchError.message : String(switchError);
						setInstanceUrlError(detail);
					}
					return;
				}
			}

			const loginUrl = new URL('/login', baseUrl);
			loginUrl.searchParams.set('desktop_handoff', '1');
			if (prefillEmail) {
				loginUrl.searchParams.set('email', prefillEmail);
			}

			await openExternalUrl(loginUrl.toString());
		}, [
			canSwitchInstanceUrl,
			currentWebAppUrl,
			handoffCode,
			i18n,
			instanceUrl,
			prefillEmail,
			switchInstanceUrl,
			targetWebAppUrl,
		]);

		return (
			<Modal.Root size="small" centered onClose={ModalActionCreators.pop}>
				<Modal.Header title={i18n._(msg`Add account`)} />
				<Modal.Content contentClassName={styles.content}>
					<p className={styles.description}>
						<Trans>Open your browser, log in, then enter the code below to link your account.</Trans>
					</p>

					{canSwitchInstanceUrl ? (
						<div className={styles.codeInputSection}>
							<Input
								label={i18n._(msg`Instance URL`)}
								value={instanceUrl}
								onChange={handleInstanceUrlChange}
								error={instanceUrlError ?? undefined}
								disabled={isGenerating}
								autoComplete="url"
								placeholder="example.com"
								footer={
									instanceUrlHelper && !instanceUrlError ? (
										<p className={styles.inputHelper}>{instanceUrlHelper}</p>
									) : null
								}
							/>
						</div>
					) : null}

					<HandoffCodeDisplay
						code={handoffCode}
						expiresAt={handoffExpiresAt}
						isGenerating={isGenerating}
						error={error}
						onRetry={generateCode}
					/>

					{prefillEmail ? (
						<p className={styles.prefillHint}>
							<Trans>We will prefill {prefillEmail} once the browser login opens.</Trans>
						</p>
					) : null}
				</Modal.Content>

				<Modal.Footer>
					<Button variant="secondary" onClick={ModalActionCreators.pop} disabled={isGenerating}>
						<Trans>Cancel</Trans>
					</Button>
					<Button variant="primary" onClick={handleOpenBrowser} submitting={isGenerating}>
						<ArrowSquareOutIcon size={16} weight="bold" />
						<Trans>Open browser</Trans>
					</Button>
				</Modal.Footer>
			</Modal.Root>
		);
	},
);

export function showBrowserLoginHandoffModal(
	onSuccess: (payload: LoginSuccessPayload) => Promise<void>,
	targetWebAppUrl?: string,
	prefillEmail?: string,
): void {
	ModalActionCreators.push(
		modal(() => (
			<BrowserLoginHandoffModal
				onSuccess={async (payload) => {
					await onSuccess(payload);
				}}
				targetWebAppUrl={targetWebAppUrl}
				prefillEmail={prefillEmail}
			/>
		)),
	);
}

export default BrowserLoginHandoffModal;
